import { GLOBAL_CALLING_CODES } from '@chiroup/core/constants/globals';
import { Patient } from '@chiroup/core/types/Patient.type';
import { ArrowPathIcon, BackspaceIcon } from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HEALTHCOM_PRIMARY } from '../../constants/brand';
import kioskService, {
  KioskClinicInfo,
  KioskRequestBootstrap,
  clearKioskId,
  getKioskId,
} from '../../services/kiosk.service';
import { RequestBootstrap } from '../../services/request.service';
import { Skeleton, SkeletonText } from '../common/Skeleton';
import RequestRunner from '../request/RequestRunner';
import StepMenu from '../request/StepMenu';

// Per-country PatternFormat masks. Mirrors the legacy `Phone`
// component in libs/components so the kiosk's phone input matches
// the rest of the app's formatting per dial code.
const phoneFormatFor = (countryCode: string): string => {
  switch (countryCode) {
    case '+1':
      return '(###) ###-####';
    case '+44':
      return '#### ######';
    case '+61':
      return '#### ######';
    case '+33':
      return '# ## ## ## ##';
    case '+49':
      return '### #######';
    case '+55':
      return '(##) #### ####';
    case '+86':
      return '### #### ####';
    case '+91':
      return '##### #####';
    case '+81':
      return '## #### ####';
    case '+52':
      return '## #### ####';
    case '+297':
      return '### ####';
    default:
      if (countryCode.length <= 3) return '### ### ####';
      if (countryCode.length <= 4) return '#### ######';

      return '#### #####';
  }
};

// Required digit count per dial code — patterns above use one `#` per
// digit, so we count those to know when the number is complete.
const requiredDigitsFor = (countryCode: string): number =>
  (phoneFormatFor(countryCode).match(/#/g) || []).length;

// Kiosk shell — three phases:
//   1) `pairing` — device isn't claimed yet. Show a 6-digit pair code
//      and poll the backend until the clinic claims it from the staff
//      app (Clinic settings → Kiosks → Pair device).
//   2) `phone` — device is paired. Patient enters their phone, and
//      we fetch their active request bootstrap. No verification —
//      the kiosk being paired IS the trust boundary.
//   3) `running` — mount RequestRunner with `kiosk={true}` so uploads
//      are filtered out. On exit (or after a brief idle window), we
//      clear local state and return to the phone screen for the next
//      patient.

type Phase =
  | 'loading'
  | 'pairing'
  | 'pickPatient'
  | 'phone'
  | 'menu'
  | 'running'
  | 'unpaired-error';

type PatientLite = Patient & { lastFirst?: string };

const POLL_PAIR_MS = 5000;

const KioskShell: React.FC = () => {
  const kioskIdRef = useRef<string>('');
  const [phase, setPhase] = useState<Phase>('loading');
  const [pairCode, setPairCode] = useState<string>('');
  const pairCodeRef = useRef<string>('');
  const [clinicInfo, setClinicInfo] = useState<KioskClinicInfo | null>(null);
  const [bootstrap, setBootstrap] = useState<RequestBootstrap | null>(null);
  const [matchedPatients, setMatchedPatients] = useState<PatientLite[] | null>(null);
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const requiredDigits = useMemo(() => requiredDigitsFor(countryCode), [countryCode]);
  const [submitting, setSubmitting] = useState(false);
  // Index into bootstrap.steps for the currently-running step. The
  // kiosk runs one step at a time off a menu rather than the full
  // sequential stepper — patients pick what they want to work on.
  const [activeStepIndex, setActiveStepIndex] = useState<number | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pairPollInFlightRef = useRef(false);

  // Lazy-init kiosk-id once on mount.
  useEffect(() => {
    kioskIdRef.current = getKioskId();
  }, []);

  // Stop polling on unmount.
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, []);

  // Hardware keyboard support — gated on the phone-entry phase so
  // pairing / menu / running screens aren't intercepted. Drives the
  // same `phone` state every keypad variant binds to, so this works
  // whether the visible pad is the built-in `KioskPhoneNumberPad` or
  // an inline variant (e.g. iPhone-style pad).
  useEffect(() => {
    if (phase !== 'phone' || submitting) return;

    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const target = document.activeElement as HTMLElement | null;

      if (target) {
        const tag = target.tagName;

        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        if (target.isContentEditable) return;
      }

      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        setPhone(prev => (prev.length < requiredDigits ? prev + e.key : prev));
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        setPhone(prev => prev.slice(0, -1));
      }
    };

    window.addEventListener('keydown', handler);

    return () => window.removeEventListener('keydown', handler);
  }, [phase, submitting, requiredDigits]);

  const primaryColor = clinicInfo?.primaryColor || HEALTHCOM_PRIMARY;

  // Pull clinic info — succeeds when paired, 404s when not.
  const fetchMe = useCallback(async (): Promise<KioskClinicInfo | null> => {
    const id = kioskIdRef.current;

    if (!id) return null;

    try {
      return await kioskService.me(id);
    } catch (err: any) {
      if (err?.status === 401 || err?.status === 404) return null;
      Sentry.captureException(err);

      return null;
    }
  }, []);

  // Initial bootstrap — me() first, fall back to pair() if not paired.
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const id = kioskIdRef.current || getKioskId();

      if (!id) return;

      kioskIdRef.current = id;
      const info = await fetchMe();

      if (cancelled) return;

      if (info?.clinicId) {
        setClinicInfo(info);
        setPhase('phone');

        return;
      }

      // Not paired — request a pair code and start polling.
      try {
        const res = await kioskService.pairInit(id);

        if (cancelled) return;

        if (res.paired) {
          // Race: clinic claimed between me() and pair(). Refetch.
          const refreshed = await fetchMe();

          if (!cancelled && refreshed?.clinicId) {
            setClinicInfo(refreshed);
            setPhase('phone');

            return;
          }
        }

        if (!res.paired) {
          pairCodeRef.current = res.pairCode;
          setPairCode(res.pairCode);
          setPhase('pairing');
          startPollingForPair();
        }
      } catch (err) {
        if (cancelled) return;
        Sentry.captureException(err);
        setPhase('unpaired-error');
      }
    };

    run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPollingForPair = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }

    // Poll the read-only `kiosk/pair/status` endpoint. Pair-init
    // (POST /kiosk/pair) is called exactly once — it owns row
    // creation + pair-code rotation. This poll just checks whether
    // the clinic has claimed the device. On the transition to
    // `paired: true` the response carries a kiosk-scoped session
    // token; the service stashes it client-side, then `me()` can
    // authenticate.
    pollTimerRef.current = setInterval(async () => {
      if (pairPollInFlightRef.current) return;

      const id = kioskIdRef.current;
      const code = pairCodeRef.current;

      if (!id || !code) return;

      pairPollInFlightRef.current = true;

      try {
        const res = await kioskService.pairStatus(id, code);

        if (res.paired) {
          if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
          }

          const info = await fetchMe();

          if (info?.clinicId) {
            setClinicInfo(info);
            pairCodeRef.current = '';
            setPairCode('');
            setPhase('phone');
          }
        }
      } catch (err) {
        // Network/server error — keep polling; the next tick will
        // retry. Don't clear the interval or abandon the device.
        Sentry.captureException(err);
      } finally {
        pairPollInFlightRef.current = false;
      }
    }, POLL_PAIR_MS);
  };

  const refreshPairCode = async () => {
    const id = kioskIdRef.current;

    if (!id) return;

    try {
      const res = await kioskService.pairInit(id);

      if (res.paired && 'clinicId' in res) {
        const info = await fetchMe();

        if (info?.clinicId) {
          setClinicInfo(info);
          setPhase('phone');
        }

        return;
      }

      if (!res.paired) {
        pairCodeRef.current = res.pairCode;
        setPairCode(res.pairCode);
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  // Submit phone — backend either returns the bootstrap or a list of
  // patients (multiple matches, kiosk picks).
  const submitPhone = async (e?: React.FormEvent) => {
    e?.preventDefault();

    const id = kioskIdRef.current;

    if (!id || phone.length < requiredDigits || submitting) return;

    setSubmitting(true);
    setPhoneError(null);

    try {
      // Legacy contract — Patient.phone is stored as `+CC NNNNNNNNNN`
      // (dial code + space + raw digits). The shared Phone component
      // in libs/components produces the same shape; the kiosk
      // backend's getPatientsByPhone does an exact match, so we need
      // to send the same.
      const formattedPhone = `${countryCode} ${phone}`;
      const res = await kioskService.getRequest({ kioskId: id, phone: formattedPhone });

      if ('patients' in res) {
        if (res.patients.length === 1) {
          await pickPatient(res.patients[0].ID);
        } else {
          setMatchedPatients(res.patients as PatientLite[]);
          setPhase('pickPatient');
        }
      } else {
        applyBootstrap(res);
      }
    } catch (err: any) {
      const code = err?.response?.data?.message || err?.message;

      if (err?.status === 404 || code === 'PATIENT_NOT_FOUND') {
        setPhoneError('No matching patient found.');
      } else {
        Sentry.captureException(err);
        setPhoneError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const pickPatient = async (patientId: string) => {
    const id = kioskIdRef.current;

    if (!id) return;

    setSubmitting(true);
    setPhoneError(null);

    try {
      const res = await kioskService.getRequest({ kioskId: id, patientId });

      if ('patients' in res) {
        // Shouldn't happen with patientId, but guard anyway.
        setMatchedPatients(res.patients as PatientLite[]);
      } else {
        applyBootstrap(res);
      }
    } catch (err) {
      Sentry.captureException(err);
      setPhoneError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const applyBootstrap = (res: KioskRequestBootstrap) => {
    setBootstrap({
      steps: res.steps,
      language: (res.language === 'es' ? 'es' : 'en') as 'en' | 'es',
      gender: res.gender as RequestBootstrap['gender'],
      age: res.age || 0,
      // The kiosk's `getRequest` doesn't tie back to a single
      // requestId since it builds the bootstrap from prMap (multiple
      // requests can be active per patient). RequestRunner only uses
      // bootstrap.id when none of the steps carry their own request
      // id, but each step does (via prMap), so id can stay empty.
      id: '',
      ccsFollowups: res.ccsFollowups,
      guardian: res.guardian,
      validationCode: res.validationCode,
      prMap: res.prMap,
    });
    setMatchedPatients(null);
    setActiveStepIndex(null);
    setPhase('menu');
  };

  const finishPatient = () => {
    // Reset to phone entry for the next patient. We keep the kioskId
    // and clinicInfo — the device stays paired.
    setBootstrap(null);
    setActiveStepIndex(null);
    setPhone('');
    setPhoneError(null);
    setPhase('phone');
  };

  // Open a single step from the menu. The runner is mounted with a
  // bootstrap containing only this step, so on completion it shows
  // the "All set" panel and exit returns to the menu.
  const openStep = (index: number) => {
    setActiveStepIndex(index);
    setPhase('running');
  };

  const exitStepToMenu = () => {
    setActiveStepIndex(null);
    setPhase('menu');
  };

  // -- Renders --

  if (phase === 'loading') {
    // Mirror the phone-entry layout (the screen patients hit on a
    // paired kiosk) so the skeleton's shape matches what's about to
    // render. Brand colors aren't known yet, so the welcome panel
    // uses a neutral gray instead of the clinic's primary.
    return (
      <KioskFrame primaryColor={primaryColor} ultrawide>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_3fr] rounded-3xl overflow-hidden ring-1 ring-gray-950/5 dark:ring-white/10 bg-white dark:bg-white/5">
          <div className="p-6 sm:p-8 [@media(max-height:820px)]:p-5 flex flex-col justify-between gap-6 bg-gray-100 dark:bg-white/5">
            <div className="flex flex-col gap-3">
              <Skeleton className="h-3 w-16" />
              <div className="flex flex-col gap-2 mt-1">
                <Skeleton className="h-7 w-32" />
                <Skeleton className="h-7 w-28" />
              </div>
              <SkeletonText lines={3} className="mt-2" />
            </div>
            <div className="hidden lg:block">
              <SkeletonText lines={2} />
            </div>
          </div>
          <div className="p-5 sm:p-7 [@media(max-height:820px)]:p-4 flex flex-col items-center gap-4 [@media(max-height:820px)]:gap-3">
            <div className="mt-4 [@media(max-height:820px)]:mt-2 flex flex-col gap-3 sm:gap-4 [@media(max-height:820px)]:gap-2 w-full max-w-xs sm:max-w-sm [@media(max-height:820px)]:max-w-xs">
              <Skeleton className="h-14 [@media(max-height:820px)]:h-12 w-full rounded-2xl" />
              <div className="grid grid-cols-3 gap-2 sm:gap-3 [@media(max-height:820px)]:gap-2 w-full">
                {Array.from({ length: 12 }).map((_, i) => (
                  <Skeleton key={i} className="aspect-square w-full rounded-2xl" />
                ))}
              </div>
            </div>
            <Skeleton className="h-12 w-full max-w-xs sm:max-w-sm [@media(max-height:820px)]:max-w-xs rounded-full" />
          </div>
        </div>
      </KioskFrame>
    );
  }

  if (phase === 'unpaired-error') {
    return (
      <KioskFrame primaryColor={primaryColor}>
        <div className="portal-card p-6 text-center">
          <p className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
            Couldn’t reach the server
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            We weren’t able to set up this device. Please check the network connection and try
            again.
          </p>
          <button
            type="button"
            onClick={() => {
              clearKioskId();
              window.location.reload();
            }}
            className="inline-flex items-center justify-center gap-1.5 rounded-full px-5 py-2 text-sm font-semibold text-white"
            style={{ backgroundColor: primaryColor }}
          >
            <ArrowPathIcon className="size-4" /> Retry
          </button>
        </div>
      </KioskFrame>
    );
  }

  if (phase === 'pairing') {
    return (
      <KioskFrame primaryColor={primaryColor}>
        <div
          className="rounded-3xl overflow-hidden p-6 sm:p-8 dark:ring-1 dark:ring-white/10 text-center"
          style={{ backgroundColor: `${primaryColor}12` }}
        >
          <p
            className="text-xs font-semibold uppercase tracking-wider"
            style={{ color: primaryColor }}
          >
            Kiosk pairing
          </p>
          <h1 className="mt-1 text-2xl sm:text-3xl font-bold tracking-tight leading-tight text-gray-900 dark:text-gray-100">
            Pair this device
          </h1>
          <p className="mt-3 text-sm text-gray-700 dark:text-gray-200 max-w-prose mx-auto">
            Enter the pair code below in ChiroUp under{' '}
            <span className="font-semibold">Clinic settings → Kiosks → Pair device</span>. This
            screen refreshes automatically once the device is paired.
          </p>
          <p
            className="mt-8 text-5xl sm:text-6xl font-bold tracking-tight tabular-nums"
            style={{ color: primaryColor }}
            aria-live="polite"
          >
            {pairCode || '------'}
          </p>
          <button
            type="button"
            onClick={refreshPairCode}
            className="mt-8 inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <ArrowPathIcon className="size-4" />
            Generate a new code
          </button>
        </div>
      </KioskFrame>
    );
  }

  if (phase === 'pickPatient' && matchedPatients) {
    return (
      <KioskFrame primaryColor={primaryColor} ultrawide>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_3fr] rounded-3xl overflow-hidden ring-1 ring-gray-950/5 dark:ring-white/10 bg-white dark:bg-white/5">
          <div
            className="p-6 sm:p-8 [@media(max-height:820px)]:p-5 flex flex-col justify-between gap-6 text-white"
            style={{ backgroundColor: primaryColor }}
          >
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-white/75">
                Welcome
              </p>
              <h1 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight leading-tight">
                Who’s checking in?
              </h1>
              <p className="mt-3 text-base text-white/90 max-w-prose">
                We found multiple patients on that phone number. Choose your name to continue.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setMatchedPatients(null);
                setPhase('phone');
              }}
              className="hidden lg:inline-flex self-start items-center gap-1.5 text-sm font-medium text-white/80 hover:text-white"
            >
              ← Back to phone entry
            </button>
          </div>

          <div className="p-5 sm:p-7 [@media(max-height:820px)]:p-4 flex flex-col gap-3">
            <ul role="list" className="flex flex-col gap-2.5">
              {matchedPatients.map(p => (
                <li key={p.ID}>
                  <button
                    type="button"
                    onClick={() => pickPatient(p.ID)}
                    disabled={submitting}
                    className="w-full text-left rounded-2xl px-5 py-4 portal-card hover:ring-2 hover:ring-[--ring-color] focus-visible:ring-2 focus-visible:ring-[--ring-color] focus-visible:outline-none transition-shadow disabled:opacity-50"
                    style={{ ['--ring-color' as any]: primaryColor } as React.CSSProperties}
                  >
                    <p className="text-base font-semibold text-gray-900 dark:text-gray-100">
                      {p.lastFirst || `${p.lname}, ${p.fname}`}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => {
                setMatchedPatients(null);
                setPhase('phone');
              }}
              className="lg:hidden self-start mt-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
            >
              ← Back to phone entry
            </button>
          </div>
        </div>
      </KioskFrame>
    );
  }

  if (phase === 'menu' && bootstrap) {
    return (
      <KioskFrame primaryColor={primaryColor} ultrawide>
        <StepMenu
          bootstrap={bootstrap}
          primaryColor={primaryColor}
          onPickStep={openStep}
          onFinish={finishPatient}
          // Kiosk hides the legacy uploads — RequestRunner with
          // kiosk=true does the same so the menu reflects what the
          // runner would actually advance through.
          filterStep={s => s.type !== 'uploadPhotoId' && s.type !== 'uploadInsuranceCard'}
          // Mid-flow exit — kiosk swaps patients here rather than
          // closing. Direct request flow omits this prop so its menu
          // only shows the finish button at the very end.
          incompleteFinishLabel={{ en: 'Switch patient', es: 'Cambiar de paciente' }}
        />
      </KioskFrame>
    );
  }

  if (phase === 'running' && bootstrap && activeStepIndex !== null) {
    const activeStep = bootstrap.steps[activeStepIndex];

    if (!activeStep) {
      // Index drifted (shouldn't normally happen) — bail to menu.
      exitStepToMenu();

      return null;
    }

    // Single-step bootstrap so RequestRunner only renders this one
    // step. When that step completes, it'll show its "All set" panel
    // and Exit returns to the menu where we can pick the next thing.
    const singleStepBootstrap: RequestBootstrap = {
      ...bootstrap,
      steps: [activeStep],
    };

    return (
      <RequestRunner
        bootstrap={singleStepBootstrap}
        primaryColor={primaryColor}
        kiosk
        singleStepMode
        onUpdateRequest={data => {
          if (!data.steps || !data.steps.length) return;

          const updatedStep = data.steps[0];
          setBootstrap(prev => {
            if (!prev) return prev;

            return {
              ...prev,
              steps: prev.steps.map((s, i) => (i === activeStepIndex ? updatedStep : s)),
            };
          });
        }}
        onExit={exitStepToMenu}
      />
    );
  }

  // Phone entry — picker round. Each option is a complete frame so
  // page-level treatments (brand-flooded background for Atrium, gray
  // page bg for the others) can vary independently. Form logic is
  // shared via component-level state; only one variant is interactable
  // at a time (others render with `hidden` so they're not in the tab
  // order and can't fire submit).
  const continueDisabled = submitting || phone.length < requiredDigits;
  const continueLabel = submitting ? 'Looking up…' : 'Continue';

  const pad = (
    <KioskPhoneNumberPad
      phone={phone}
      onPhoneChange={setPhone}
      countryCode={countryCode}
      onCountryCodeChange={code => {
        setCountryCode(code);
        setPhone('');
      }}
      requiredDigits={requiredDigits}
      phoneFormat={phoneFormatFor(countryCode)}
      primaryColor={primaryColor}
      disabled={submitting}
    />
  );

  return (
    <KioskFrame primaryColor={primaryColor} ultrawide>
      <form
        onSubmit={submitPhone}
        className="grid grid-cols-1 lg:grid-cols-[1fr_3fr] rounded-3xl overflow-hidden ring-1 ring-gray-950/5 dark:ring-white/10 bg-white dark:bg-white/5"
      >
        <div
          className="p-6 sm:p-8 [@media(max-height:820px)]:p-5 flex flex-col justify-between gap-6 text-white"
          style={{ backgroundColor: primaryColor }}
        >
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-white/75">Welcome</p>
            <h1 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight leading-tight">
              Let's get you checked in.
            </h1>
            <p className="mt-3 text-base text-white/90 max-w-prose">
              Enter the phone number on file. We'll pull up your visit and any forms we need from
              you.
            </p>
          </div>
          <div className="hidden lg:block">
            <p className="text-sm text-white/80">
              Need help? A team member can assist you at the front desk.
            </p>
          </div>
        </div>

        <div className="p-5 sm:p-7 [@media(max-height:820px)]:p-4 flex flex-col items-center gap-4 [@media(max-height:820px)]:gap-3">
          {pad}
          {phoneError && <p className="text-sm text-red-600 dark:text-red-400">{phoneError}</p>}
          <button
            type="submit"
            disabled={continueDisabled}
            className="w-full max-w-xs sm:max-w-sm [@media(max-height:820px)]:max-w-xs inline-flex items-center justify-center rounded-full px-7 py-3 text-base sm:text-lg font-semibold text-white disabled:opacity-50 active:scale-[0.98] transition-transform"
            style={{ backgroundColor: primaryColor }}
          >
            {continueLabel}
          </button>
        </div>
      </form>
    </KioskFrame>
  );
};

// On-screen number pad. Built specifically for the kiosk so older
// patients don't have to deal with iPad's on-screen keyboard. Big
// rounded keys (76px+ on touch viewports), live-formatted phone
// display sits at the top, dial code lives in a small chip on the
// left so it stays out of the way but is still adjustable.
//
// Hidden text input keeps the form's native `tel` accessibility +
// browser autofill working — the visible display reflects whatever
// the input contains.
const KioskPhoneNumberPad: React.FC<{
  phone: string;
  onPhoneChange: (next: string) => void;
  countryCode: string;
  onCountryCodeChange: (next: string) => void;
  requiredDigits: number;
  phoneFormat: string;
  primaryColor: string;
  disabled?: boolean;
}> = ({
  phone,
  onPhoneChange,
  countryCode,
  onCountryCodeChange,
  requiredDigits,
  phoneFormat,
  primaryColor,
  disabled,
}) => {
  const tap = (digit: string) => {
    if (disabled) return;
    if (phone.length >= requiredDigits) return;
    onPhoneChange(phone + digit);
  };

  const backspace = () => {
    if (disabled) return;
    if (!phone.length) return;
    onPhoneChange(phone.slice(0, -1));
  };

  // Build the formatted display by walking the format mask and slot-
  // ting digits in. Empty slots show as a faint bullet so the user
  // can see how many digits are left to type. `slot` flags digit
  // positions (vs. format chars) so we can give them a fixed-width
  // slot — bullets are narrower than digits, so without this the
  // display reflows (and drags the keypad with it) every keypress.
  const displayChars: { ch: string; filled: boolean; slot: boolean }[] = [];
  let digitCursor = 0;

  for (const ch of phoneFormat) {
    if (ch === '#') {
      const d = phone[digitCursor];

      digitCursor += 1;
      displayChars.push({ ch: d ?? '•', filled: !!d, slot: true });
    } else {
      // Format characters (parens, hyphens, spaces) always render in
      // the muted style — only digit slots take on the filled color
      // as the patient types.
      displayChars.push({ ch, filled: false, slot: false });
    }
  }

  return (
    <div className="mt-4 [@media(max-height:820px)]:mt-2 flex flex-col gap-3 sm:gap-4 [@media(max-height:820px)]:gap-2 w-full max-w-xs sm:max-w-sm [@media(max-height:820px)]:max-w-xs">
      {/* Live display — big tabular-nums readout above the pad.
          Width is fixed by the parent so the display doesn't reflow
          (and pull the keypad with it) as digits replace bullets. */}
      <div
        className="rounded-2xl bg-white dark:bg-white/5 ring-1 ring-gray-950/5 dark:ring-white/10 p-3 sm:p-4 [@media(max-height:820px)]:p-2.5 flex items-center gap-3"
        aria-live="polite"
      >
        {/* Dial-code chip (tap to change). Default is +1 — most
            kiosks won't ever need anything else, so we keep it
            visually quiet but present. */}
        <label className="relative flex items-center gap-1 rounded-xl bg-gray-100 dark:bg-white/10 px-3 py-1.5 text-sm sm:text-base font-semibold tabular-nums text-gray-700 dark:text-gray-200 cursor-pointer shrink-0">
          <span>{countryCode}</span>
          <svg
            viewBox="0 0 8 5"
            width="8"
            height="5"
            fill="none"
            className="opacity-60"
            aria-hidden="true"
          >
            <path d="M.5.5 4 4 7.5.5" stroke="currentcolor" />
          </svg>
          <select
            aria-label="Country dial code"
            value={countryCode}
            disabled={disabled}
            onChange={e => onCountryCodeChange(e.target.value)}
            className="absolute inset-0 opacity-0 cursor-pointer"
          >
            {GLOBAL_CALLING_CODES.map(code => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </label>

        {/* The big formatted display. Digit slots get a fixed `1ch`
            width so bullets and digits occupy the same space — the
            display width stays constant whether the patient has
            typed 0 or 10 digits. */}
        <div className="flex-1 text-center text-2xl sm:text-3xl [@media(max-height:820px)]:text-2xl font-bold tabular-nums text-gray-900 dark:text-gray-100 select-none truncate">
          {displayChars.length ? (
            displayChars.map((c, i) => (
              <span
                key={i}
                className={`inline-block ${c.slot ? 'w-[1ch] text-center' : ''} ${c.filled ? '' : 'text-gray-300 dark:text-gray-600'}`}
              >
                {c.ch}
              </span>
            ))
          ) : (
            <span className="text-gray-300 dark:text-gray-600">•••••••••</span>
          )}
        </div>

        {/* Hidden tel input — keeps form/autocomplete behavior
            working. */}
        <input type="hidden" name="phone" value={`${countryCode} ${phone}`} />
      </div>

      {/* The pad itself — capped to a comfortable touch-target size
          (~120px keys at max-w-xs) so the whole thing always fits a
          landscape iPad without scrolling. Bigger phones / portrait
          tablets show it centered with whitespace on either side. On
          shorter viewports (iPad mini landscape ~768px tall) we cap
          back to max-w-xs so the keys don't crowd the form. */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3 mx-auto w-full max-w-xs sm:max-w-sm [@media(max-height:820px)]:max-w-xs [@media(max-height:820px)]:gap-2">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(d => (
          <KeyButton key={d} onPress={() => tap(d)} disabled={disabled} primaryColor={primaryColor}>
            {d}
          </KeyButton>
        ))}
        <span aria-hidden="true" />
        <KeyButton onPress={() => tap('0')} disabled={disabled} primaryColor={primaryColor}>
          0
        </KeyButton>
        <KeyButton
          onPress={backspace}
          disabled={disabled || !phone.length}
          primaryColor={primaryColor}
          ariaLabel="Backspace"
        >
          <BackspaceIcon className="size-7 sm:size-8 mx-auto" />
        </KeyButton>
      </div>
    </div>
  );
};

const KeyButton: React.FC<{
  onPress: () => void;
  disabled?: boolean;
  primaryColor: string;
  children: React.ReactNode;
  ariaLabel?: string;
}> = ({ onPress, disabled, primaryColor, children, ariaLabel }) => (
  <button
    type="button"
    onClick={onPress}
    disabled={disabled}
    aria-label={ariaLabel}
    className="aspect-square w-full rounded-2xl bg-white dark:bg-white/5 ring-1 ring-gray-950/10 dark:ring-white/15 text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-gray-100 hover:bg-[--brand-tint] active:bg-[--brand-tint-strong] active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[--brand] focus-visible:ring-offset-2 disabled:opacity-40 disabled:active:scale-100 transition-all touch-manipulation select-none"
    style={
      {
        ['--brand' as any]: primaryColor,
        ['--brand-tint' as any]: `${primaryColor}15`,
        ['--brand-tint-strong' as any]: `${primaryColor}28`,
      } as React.CSSProperties
    }
  >
    {children}
  </button>
);

const KioskFrame: React.FC<{
  primaryColor: string;
  // Width tiers, picked to match the kiosk's likely landscape iPad
  // home: `ultrawide` for the phone-entry split panel (uses the full
  // device width via a 2-col layout), `wide` for screens that benefit
  // from extra horizontal space (menu, patient picker), default for
  // narrow content (pairing code / loading).
  wide?: boolean;
  ultrawide?: boolean;
  children: React.ReactNode;
}> = ({ wide, ultrawide, children }) => (
  <main className="min-h-dvh flex flex-col items-center justify-center antialiased bg-gray-50 dark:bg-[var(--color-bg-page)] px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
    <div className={`w-full ${ultrawide ? 'max-w-5xl' : wide ? 'max-w-3xl' : 'max-w-md'}`}>
      {children}
    </div>
  </main>
);

export { KioskShell };

export default KioskShell;
